<script lang="ts" setup>
import { useVueI18n } from '../../locales'
  import { AlertCircleIcon, CreditCard02Icon } from '@gohighlevel/ghl-icons/24/outline'
  import { UITextMdNormal, UITextSmRegular, UITooltip } from '@gohighlevel/ghl-ui'
  import { computed, nextTick, onMounted, PropType, ref } from 'vue'
  import { IElement, IRecipient, MovableComponentEvents, SaveCardDetailsElementOptions } from '../../types'
  import { getSignatureElementColorStyles } from '../../utils'
  import {
    isDefaultPaymentProviderConfigured,
    isDefaultPaymentProviderSupportedForSaveCard,
  } from '../../utils/isDefaultPaymentProviderConfigured'
  import { MovableComponentWrapper } from '../MovableComponentWrapper'
  import { CommonTooltip } from '../Tooltip'
  const t = useVueI18n()

  const props = defineProps({
    element: {
      type: Object as PropType<IElement<SaveCardDetailsElementOptions>>,
      required: true,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    snapContainer: {
      type: String,
    },
    boundContainer: {
      type: String,
    },
    storeMap: {
      type: Object,
      required: true,
    },
    compositionMap: {
      type: Object,
      required: true,
    },
  })
  const enableTooltip = ref(true)
  const isActiveElement = computed(() => props.isActive)
  const builderStore = props.storeMap.useBuilderStore()
  const emits = defineEmits([MovableComponentEvents.ON_ACTIVE])
  const { updateStyles, updateOption } = props.compositionMap.useElementOption()
  const store = props.storeMap.useAppStore()
  const isPaymentProviderConfigured = computed(() =>
    isDefaultPaymentProviderConfigured(store.paymentIntegrations?.defaultPaymentProvider, store.document?.paymentLiveMode ?? true)
  )
  const isPaymentProviderSupportedForSaveCard = computed(() =>
    isDefaultPaymentProviderSupportedForSaveCard(store.paymentIntegrations?.defaultPaymentProvider)
  )
  const isSaveCardPaymentReady = computed(() => isPaymentProviderConfigured.value && isPaymentProviderSupportedForSaveCard.value)
  const recipient = computed(() => {
    const recipients = store.document?.recipients || []
    return recipients.find((r: IRecipient) => r.id === props.element?.component.options.recipient) || ({} as IRecipient)
  })
  const recipientName = computed(() => {
    const { firstName, lastName } = recipient.value
    return firstName ? `${firstName} ${lastName || ''}` : ''
  })
  const onDrag = (styles: any) => {
    updateStyles('position', styles, props.element?.id)
    updateOption('isGhost', true, props.element?.id)
  }
  const onDragUpdate = (value: boolean) => {
    enableTooltip.value = !value
  }
  const onScale = ({ scaleX, scaleY }: { scaleX: number; scaleY: number }) => {
    updateStyles('scale', { scaleX, scaleY }, props.element?.id)
  }
  const onResize = ({ height, width }: { height: number; width: number }) => {
    updateStyles('dimensions', { height, width }, props.element?.id)
  }
  const onActive = (e: any, isActive: boolean) => {
    emits(MovableComponentEvents.ON_ACTIVE, e, isActive)
    if (isActive) {
      hideCustomValues()
    }
  }
  function del(e: MouseEvent) {
    e.stopPropagation()
    builderStore.deleteElement(props?.element?.id as string)
    builderStore.syncRecipient()
    builderStore.syncCustomFieldsForLinkage()
  }
  const isDropdownReady = ref(false)
  onMounted(async () => {
    builderStore.syncRecipient()
    await nextTick()
    isDropdownReady.value = true
  })
  function clone(e: any) {
    e.stopPropagation()
    builderStore.cloneElement(props?.element?.id as string)
  }
  const styles = computed(() => getSignatureElementColorStyles(recipient.value))
  const colorStyles = computed(() => {
    const dimensionsObj = props.element.responsiveStyles.large.dimensions
    return {
      border: `1px solid ${styles.value.borderColor} !important`,
      height: `${(dimensionsObj?.height as number) - 2}px`,
      backgroundColor: styles.value.backgroundColor,
      color: styles.value.placeholderColor,
      gridTemplateColumns: '0fr 1fr',
    }
  })
  const position = computed(() => {
    const pos = props.element?.responsiveStyles.large.position
    return pos || { top: 0, left: 0, bottom: 0, right: 0 }
  })
  const scale = computed(() => {
    const scaleObj = props.element?.responsiveStyles.large.scale
    return scaleObj || { scaleX: 1, scaleY: 1 }
  })
  const dimensions = computed(() => {
    const dimensionsObj = props.element?.responsiveStyles.large.dimensions
    return dimensionsObj || { height: 'auto', width: 'auto' }
  })
  const align = computed(() => props.element?.responsiveStyles.large.align)
  const onDragEnd = (options: any) => {
    builderStore.moveFloatingElement(props.element?.id as string, options)
    onDrag({
      top: options.styles.top,
      left: options.styles.left,
      bottom: options.styles.bottom,
      right: options.styles.right,
      height: options.styles.height,
      width: options.styles.width,
      preferBottom: options.styles.preferBottom,
      preferRight: options.styles.preferRight,
    })
  }
  const show = ref(false)
  const hideCustomValues = () => {
    show.value = false
  }

  const placeHolderText = computed(() => {
    return props.element?.component.options.text || props.element?.component.options.placeholder
  })
</script>

<template>
  <MovableComponentWrapper
    :key="`moveable-element-${props.element?.id}`"
    :id="props.element?.id as string"
    @on-drag="onDrag"
    @on-scale="onScale"
    @on-active="onActive"
    @on-resize="onResize"
    @on-drag-update="onDragUpdate"
    @on-drag-end="onDragEnd"
    :is-active-element="isActiveElement"
    :is-editable="builderStore.isDocumentEditable"
    :position="position"
    :scale="scale"
    :align="align"
    :dimensions="dimensions"
    :is-ghost="props.element?.component.options.isGhost"
    :snap-container="props.snapContainer"
    :bound-container="props.boundContainer"
    :is-drag-element="props.element?.component.isDraggable"
    :resizable="true"
    :keep-ratio="false"
    :render-directions="['se']"
    :min-height="40"
    :min-width="40"
    :color-override="styles.placeholderColor"
    :inactive-color-override="styles.borderColor"
  >
    <template #component>
      <span v-if="element?.component.options.required" class="option--required">
        {{ t('common.requiredIndicator') }}
      </span>
      <div class="el-wrapper collect-documents-el relative" style="height: inherit; width: inherit">
        <div :style="colorStyles" class="rounded-sm">
          <div class="grid gap-2 px-2 items-center" style="grid-template-columns: 0fr 1fr">
            <CreditCard02Icon class="w-5 h-5 py-2" v-if="isSaveCardPaymentReady" />
            <UITooltip placement="top" v-else>
              <UITextSmRegular v-if="!isPaymentProviderConfigured" class="py-2 text-ellipsis">
                {{ t('validations.warnings.noIntegrationFound') }}
              </UITextSmRegular>
              <UITextSmRegular v-else class="py-2 text-ellipsis">
                {{ t('validations.paymentProvider.unsupportedSaveCardDescription') }}
              </UITextSmRegular>
              <template #trigger>
                <AlertCircleIcon class="w-5 h-5 py-2 text-red-500" />
              </template>
            </UITooltip>
            <UITextMdNormal class="py-2 text-ellipsis">{{ placeHolderText }}</UITextMdNormal>
          </div>
        </div>
      </div>
    </template>
    <template #popup-options>
      <CommonTooltip @on-clone="clone" @on-delete="del" :key="`floating-options-${props.element?.id}`" />
    </template>

    <template #tooltip v-if="recipientName">
      <UITextSmRegular class="capitalize font-semibold py-1 px-4 text-xs" :style="{ color: styles.placeholderColor }">
        {{ recipientName }}
      </UITextSmRegular>
    </template>
  </MovableComponentWrapper>
</template>

<style lang="scss">
  .option--required {
    position: absolute;
    right: -8px;
    top: -19px;
    color: red;
    font-size: 22px;
    font-weight: 700;
  }

  .text-ellipsis {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
</style>
